import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { memo } from 'react';

interface InstructionDownloadEntry {
    timestamp: string;
    ipAddress: string;
    userAgent: string;
    type: 'view' | 'pdf' | 'email';
}

interface InstructionDownloadHistoryProps {
    downloads: InstructionDownloadEntry[];
    downloadCount: number;
    lastDownload?: string;
}

const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getTypeLabel = (type: string) => {
    switch (type) {
        case 'view':
            return 'Viewed';
        case 'pdf':
            return 'Downloaded PDF';
        case 'email':
            return 'Via email';
        default:
            return type;
    }
};

const getTypeColor = (type: string) => {
    switch (type) {
        case 'view':
            return '#6366f1';
        case 'pdf':
            return '#22c55e';
        case 'email':
            return '#f59e0b';
        default:
            return '#374151';
    }
};

const InstructionDownloadHistory = memo(
    ({ downloads, downloadCount, lastDownload }: InstructionDownloadHistoryProps) => {
        if (downloadCount === 0) {
            return (
                <Box
                    sx={{
                        border: '2px solid #374151',
                        backgroundColor: '#1a1a2e',
                        p: 2,
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            fontSize: '0.875rem',
                            mb: 1,
                        }}
                    >
                        Instruction Downloads
                    </Typography>
                    <Typography sx={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                        Guest has not accessed instructions yet.
                    </Typography>
                </Box>
            );
        }

        return (
            <Box
                sx={{
                    border: '2px solid #22c55e',
                    backgroundColor: '#1a1a2e',
                    p: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                        borderBottom: '1px solid #374151',
                        pb: 1,
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            fontSize: '0.875rem',
                        }}
                    >
                        Instruction Downloads
                    </Typography>
                    <Box
                        sx={{
                            display: 'inline-block',
                            px: 2,
                            py: 0.5,
                            border: '2px solid',
                            borderColor: 'success.main',
                            backgroundColor: 'rgba(34, 197, 94, 0.1)',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            color: 'success.main',
                        }}
                    >
                        {downloadCount} {downloadCount === 1 ? 'download' : 'downloads'}
                    </Box>
                </Box>
                {lastDownload && (
                    <Typography sx={{ color: '#9ca3af', fontSize: '0.75rem', mb: 2 }}>
                        Last accessed: {formatDate(lastDownload)}
                    </Typography>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {downloads
                        .slice()
                        .reverse()
                        .map((download, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    p: 1.5,
                                    backgroundColor: '#0f0f23',
                                    border: '1px solid #374151',
                                    gap: 0.5,
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#d1d5db' }}>
                                        {formatDate(download.timestamp)}
                                    </Typography>
                                    <Box
                                        sx={{
                                            px: 1,
                                            py: 0.25,
                                            border: '1px solid',
                                            borderColor: getTypeColor(download.type),
                                            color: getTypeColor(download.type),
                                            fontWeight: 600,
                                            fontSize: '0.625rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                        }}
                                    >
                                        {getTypeLabel(download.type)}
                                    </Box>
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: '0.625rem',
                                        color: '#6b7280',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {download.ipAddress}
                                </Typography>
                            </Box>
                        ))}
                </Box>
            </Box>
        );
    }
);

export default InstructionDownloadHistory;
